import { supabase } from '@sixam/supabase/client';
import type { Database, Json } from '@sixam/shared/database.types';
import { systemNowMs } from '@sixam/shared/clock';
import {
  normalizeProfileLinks,
  profileLinksToText,
  type ProfileLink,
} from '@sixam/shared/member-profile-core';
import { firstContentSafetyFailure } from '@sixam/shared/content-safety-core';

export type Profile = Database['public']['Tables']['profiles']['Row'];

export type ProfileUpdateInput = Readonly<{
  bio: string | null;
  fullName: string | null;
  links: ProfileLink[];
  phone: string | null;
  phoneIsPublic: boolean;
}>;

export { normalizeProfileLinks, profileLinksToText, type ProfileLink };

export async function updateMyProfile(userId: string, input: ProfileUpdateInput): Promise<Profile> {
  const unsafeText = firstContentSafetyFailure([
    { field: 'name', value: input.fullName },
    { field: 'bio', value: input.bio },
  ]);
  if (unsafeText) {
    throw new Error(unsafeText.message);
  }

  const { data, error } = await supabase
    .from('profiles')
    .update({
      bio: input.bio,
      full_name: input.fullName,
      links: input.links as unknown as Json,
      phone: input.phone,
      phone_is_public: input.phoneIsPublic,
    })
    .eq('id', userId)
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return data;
}

export async function uploadProfileAvatar(
  userId: string,
  asset: {
    fileName?: string | null;
    mimeType?: string | null;
    uri: string;
  }
): Promise<string> {
  const extension = asset.fileName?.split('.').pop()?.toLowerCase() ?? 'jpg';
  const path = `${userId}/avatar-${systemNowMs()}.${extension}`;
  const blob = await fetch(asset.uri).then((response) => response.blob());
  const { error } = await supabase.storage
    .from('profile-avatars')
    .upload(path, blob, { contentType: asset.mimeType ?? 'image/jpeg', upsert: true });

  if (error) {
    throw error;
  }

  const { data } = supabase.storage.from('profile-avatars').getPublicUrl(path);
  const publicUrl = data.publicUrl;
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ avatar_url: publicUrl })
    .eq('id', userId);

  if (updateError) {
    throw updateError;
  }

  return publicUrl;
}
